import { useEffect, useState } from 'react'
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '@cutilei/ui'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import Phone from 'services/Phone'
import Address from 'services/Address'
import CutileiLogo from 'icons/logo-cutilei-simple.png'
import AlertDialog from 'components/AlertDialog'
import { ReactComponent as ButtonLoading } from 'icons/loading.svg'
import { DateTime } from 'luxon'

import {
  Container,
  Image,
  Row,
  SubTitle,
  InfoText,
  LinkText,
  Block,
  DangerButton,
  ButtonColumn
} from './styles'

export default function MemberDetailsModal ({
  visible,
  data: memberData,
  onUpdate: handleUpdate = () => null,
  onClose: handleClose = () => null
}) {
  const [member, setMember] = useState (memberData)
  const [canceling, setCanceling] = useState (false)
  const [showCancelDialog, setShowCancelDialog] = useState (false)
  const token = AuthService.getToken ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    setMember (memberData)
  }, [memberData])

  const toggleSubscription = async () => {
    try {
      setCanceling (true)

      let updatedMember
      const filters = `member_id=${member.id}`
      const { data: subscription } = await cutileiApi.get (`/subscriptions?${filters}`, requestConfig)

      if (member.status === 'active') {
        const { data } = await cutileiApi.delete (
          `/subscriptions/${subscription.id}?${filters}`, requestConfig
        )
        updatedMember = data
      } else if (member.status === 'canceled') {
        const { data } = await cutileiApi.post (
          `/subscriptions/${subscription.id}/reactivate?${filters}`, {}, requestConfig
        )
        updatedMember = data
      }

      setMember (updatedMember)
      handleUpdate (updatedMember)
    } catch (error) {
      logApiError (error)
    } finally {
      setCanceling (false)
    }
  }

  const toggleCancelDialog = () => setShowCancelDialog (!showCancelDialog)

  if (!member) return null

  const { canceled_subscriptions } = member
  const address = Address.formatAddress (member.addresses[0])
  const mapsAddress = Address.formatAddressWithNoComplement (member.addresses[0])
  const addressUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent (mapsAddress)}`

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='2xl' aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{member.name}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Container>
            <Block style={{marginBottom: 0}}>
              <Image src={member.profile_picture ?? CutileiLogo}/>
              <Block>
                <Row>
                  <SubTitle>E-mail:</SubTitle>
                  <InfoText>{member.email}</InfoText>
                </Row>
                <Row>
                  <SubTitle>Contato:</SubTitle>
                  <InfoText>{Phone.formatPhone (member.phone, true)}</InfoText>
                </Row>
                <Row>
                  <SubTitle>CPF:</SubTitle>
                  <InfoText>{member.cpf}</InfoText>
                </Row>
                <Row>
                  <SubTitle>Gênero:</SubTitle>
                  <InfoText>{member.gender}</InfoText>
                </Row>
                <Row>
                  <SubTitle>Nascimento:</SubTitle>
                  <InfoText>
                    {DateTime.fromISO (member.birth).toFormat ('dd/MM/yyyy')}
                  </InfoText>
                </Row>
              </Block>
              <Block>
                <SubTitle>Endereço:</SubTitle>
                <LinkText href={addressUrl} target='_blank' rel='noopener noreferrer'>
                  <SubTitle>{address}</SubTitle>
                </LinkText>
              </Block>
              <Block>
                <Row>
                  <SubTitle>Cadastro:</SubTitle>
                  <InfoText>
                    {DateTime.fromISO (member.created_at).toFormat ('dd/MM/yyyy - H:mm:ss')}
                  </InfoText>
                </Row>
                <Row>
                  <SubTitle>Última alteração:</SubTitle>
                  <InfoText>
                    {DateTime.fromISO (member.updated_at).toFormat ('dd/MM/yyyy - H:mm:ss')}
                  </InfoText>
                </Row>
              </Block>
              {canceled_subscriptions && (
                <Block style={{marginBottom: 5}}>
                  <SubTitle style={{color: '#FF3939', marginBottom: 5}}>
                    Cliente cancelado
                  </SubTitle>
                  <Row>
                    <SubTitle>Motivo do cancelamento:</SubTitle>
                    <InfoText>
                      {canceled_subscriptions[0].reason}
                    </InfoText>
                  </Row>
                  {canceled_subscriptions[0].comment && (
                    <Row>
                      <SubTitle>Comentário:</SubTitle>
                      <InfoText>
                        {canceled_subscriptions[0].comment}
                      </InfoText>
                    </Row>
                  )}
                </Block>
              )}
              <ButtonColumn>
                <DangerButton
                  onClick={toggleCancelDialog}
                  $destructive={member.status === 'active'}
                >
                  {canceling ? <ButtonLoading/> : (
                    member.status === 'active' ? 'Cancelar assinatura' : 'Reativar a assinatura'
                  )}
                </DangerButton>
              </ButtonColumn>
              <AlertDialog
                visible={showCancelDialog}
                title='Atenção!'
                message={
                  `Deseja realmente ${member.status === 'active' ? 'cancelar' : 'reativar'} a assinatura deste cliente?`
                }
                confirmText='Sim'
                cancelText='Não'
                onConfirm={toggleSubscription}
                onClose={toggleCancelDialog}
                dangerous={member.status === 'active'}
              />
            </Block>
          </Container>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
